import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { PlusCircle, Pencil, Trash2, ChevronUp, ChevronDown, Info } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCategories, deleteCategory, addCategory, updateCategory, reorderCategory } from '../../services/category';
import { Button } from '../ui/Button';
import { CategoryModal } from '../category/CategoryModal';
import { toast } from 'react-hot-toast';

interface SidebarProps {
  onClose?: () => void;
}

export default function Sidebar({ onClose }: SidebarProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const categoryMutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      if (selectedCategory) {
        return updateCategory(selectedCategory, data.name);
      }
      return addCategory(data.name);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setIsModalOpen(false);
      toast.success(selectedCategory ? 'Category updated!' : 'Category created!');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to save category');
    },
  });

  const { data: customCategories = [] } = useQuery({
    queryKey: ['categories'],
    queryFn: getCategories,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      toast.success('Category deleted successfully');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete category');
    },
  });

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      await deleteMutation.mutateAsync(id);
    }
  };

  const reorderMutation = useMutation({
    mutationFn: ({ id, newOrder }: { id: string; newOrder: number }) =>
      reorderCategory(id, newOrder),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categories'] });
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to reorder category');
    },
  });

  const handleReorder = async (id: string, direction: 'up' | 'down') => {
    const category = customCategories.find(c => c.id === id);
    if (!category) return;

    const newOrder = direction === 'up' 
      ? Math.max(0, category.order - 1)
      : Math.min(customCategories.length - 1, category.order + 1);

    await reorderMutation.mutateAsync({ id, newOrder });
  };

  return (
    <aside className="h-full overflow-y-auto overscroll-contain">
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Categories</h2>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => {
              setSelectedCategory(null);
              setIsModalOpen(true);
            }}
            className="!p-1"
          >
            <PlusCircle className="h-4 w-4" />
          </Button>
        </div>
        <nav className="space-y-1 relative">
          {customCategories.map((category) => (
            <div key={category.id} className="flex items-center group">
              <NavLink
                to={`/?category=${category.name}`}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex-1 block px-4 py-2 rounded-md text-sm transition-colors relative z-10 ${
                    isActive
                      ? 'bg-indigo-50 text-indigo-600 font-medium dark:text-white greenchy:text-emerald-800'
                      : 'text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:text-white greenchy:text-emerald-600 greenchy:hover:text-emerald-800'
                  }`
                }
              >
                {category.name}
              </NavLink>
              <div className="hidden group-hover:flex items-center gap-1 pr-2 bg-white">
                <button
                  onClick={() => handleReorder(category.id, 'up')}
                  disabled={category.order === 0}
                  className={`p-1 ${category.order === 0 ? 'text-gray-300' : 'text-gray-400 hover:text-indigo-600'}`}
                >
                  <ChevronUp className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleReorder(category.id, 'down')}
                  disabled={category.order === customCategories.length - 1}
                  className={`p-1 ${category.order === customCategories.length - 1 ? 'text-gray-300' : 'text-gray-400 hover:text-indigo-600'}`}
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                <button
                  onClick={() => {
                    setSelectedCategory(category.id);
                    setIsModalOpen(true);
                  }}
                  className="p-1 text-gray-400 hover:text-indigo-600"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(category.id)}
                  className="p-1 text-gray-400 hover:text-red-600"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </nav>
        <div className="mt-auto pt-6 border-t border-gray-200 mt-6">
          <NavLink
            to="/about"
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center px-4 py-2 rounded-md text-sm transition-colors ${
                isActive
                  ? 'bg-indigo-50 text-indigo-600 font-medium'
                  : 'text-gray-600 hover:bg-gray-50'
              }`
            }
          >
            <Info className="h-4 w-4 mr-2" />
            About
          </NavLink>
        </div>
        <CategoryModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedCategory(null);
          }}
          onSubmit={categoryMutation.mutateAsync}
          category={customCategories.find(c => c.id === selectedCategory)}
          isLoading={categoryMutation.isPending}
        />
      </div>
    </aside>
  );
}